
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Upload, X } from "lucide-react";

interface FileUploadProps {
  label: string;
  accept: string;
  required?: boolean;
  value?: File;
  onChange: (file: File | null) => void;
  disabled?: boolean;
}

export const FileUpload = ({ label, accept, required, value, onChange, disabled = false }: FileUploadProps) => {
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    onChange(file);
  };

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">
        {label} {required && <span className="text-red-500">*</span>}
      </Label>
      <div className={`border-2 border-dashed rounded-lg p-4 text-center ${
        disabled ? 'border-gray-200 bg-gray-50' : 'border-gray-300'
      }`}>
        {value ? (
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{value.name}</span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange(null)}
              disabled={disabled}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div>
            <Upload className={`h-8 w-8 mx-auto mb-2 ${disabled ? 'text-gray-300' : 'text-gray-400'}`} />
            <input
              type="file"
              accept={accept}
              onChange={handleFileChange}
              className="hidden"
              id={`file-${label.replace(/\s+/g, '-').toLowerCase()}`}
              disabled={disabled}
            />
            <Label
              htmlFor={`file-${label.replace(/\s+/g, '-').toLowerCase()}`}
              className={`${disabled ? 'cursor-not-allowed text-gray-400' : 'cursor-pointer text-blue-600 hover:text-blue-700'}`}
            >
              {disabled ? 'Upload disabled' : 'Click to upload or drag and drop'}
            </Label>
            <p className={`text-xs mt-1 ${disabled ? 'text-gray-400' : 'text-gray-500'}`}>
              Supported: {accept.split(',').join(', ')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// eslint-disable-next-line react-refresh/only-export-components
export const emiratesOptions = [
  "Abu Dhabi",
  "Dubai", 
  "Sharjah",
  "Ajman",
  "Umm Al-Quwain",
  "Ras Al Khaimah",
  "Fujairah"
];

interface FormFieldProps {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}

export const FormField = ({ label, required, children }: FormFieldProps) => (
  <div className="space-y-2">
    <Label className="text-sm font-medium">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    {children}
  </div>
);

export default FormField;
